import { Box, Card, Typography } from "@material-ui/core";
import moment from "moment";
import "moment/locale/es";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import styled from "styled-components";
import uuidv4 from "uuid/v4";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import useLocalStorage from "../../../../util/useLocalStorage";
import Note from "./Note";
import NoteAuthor from "./NoteAuthor";
import NotesEmpty from "./NotesEmpty";
import NotesForm from "./NotesForm";
import NotesLoading from "./NotesLoading";
import useAddNoteMutation from "./useAddNoteMutation";
import useNotesQuery from "./useNotesQuery";
import useRemoveNoteMutation from "./useRemoveNoteMutation";

const StyledNoteCard = styled(Card)`
  padding: 0.25rem;
  font-family: "Source Sans Pro", Helvetica, Arial, sans-serif;
  margin: 0.375rem 0;

  background-color: #fff;

  .CMuiNoteAuthor {
    font-size: 0.8125rem;
    line-height: 1.25;
    font-weight: 600;
    margin-bottom: 0.3125rem;
  }

  .CMuiNotesCardNoteDetails {
    font-size: 0.75rem;
    line-height: 1.25;
    opacity: 0.65;
    margin-top: 0.3125rem;
    text-align: right;
  }
`;

const StyledWrapper = styled(Box)`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledContentWrapper = styled.div`
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: scroll;
  box-sizing: border-box;
  padding: 0 0.75rem 0.25rem;

  ::-webkit-scrollbar {
    width: 0.375rem;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(0, 72, 120, 0.25);
    width: 0.375rem;
    border-radius: 0.25rem;
  }
`;

const StyledFormWrapper = styled(Box)`
  padding: 0.25rem 0.75rem 1rem;
`;

let container: any;

export type TNote = {
  id: string;
  createdAt?: string;
  author?: string;
  note: string;
};

interface INotesProps {
  parentId: string;
  moduleName: string;
  notesQuery?: any;
}

const Notes = (props: INotesProps) => {
  const { parentId, moduleName } = props;

  const [notes, setNotes] = useState<TNote[]>([]);
  const [state, setState] = useState({
    open: false,
    mode: "add" as "add" | "edit",
    hasScrolledAtStart: false,
  });
  const bottomRef = useRef<HTMLDivElement>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const localNotesQuery = useNotesQuery(
    parentId,
    moduleName,
    !props.notesQuery
  );
  const notesQuery = props.notesQuery ?? localNotesQuery;

  const addNoteMutation = useAddNoteMutation(parentId, moduleName);
  const removeNoteMutation = useRemoveNoteMutation(parentId, moduleName);
  // eslint-disable-next-line
  const [notesOrder, setNotesOrder] = useLocalStorage("notes_order", "ASC");

  const staffMentions = useSelector(
    (state: any) => state.usuariosState.staffMentions
  );
  const activeStaffMentions = staffMentions.filter(
    (sm: any) => !!sm.active && !sm.id.includes("undefined")
  );

  useEffect(() => {
    const parsedNotes = [] as TNote[];

    if (notesQuery.data) {
      notesQuery.data.forEach((item: any) => {
        if (item.Note_Content) {
          parsedNotes.push({
            id: item.id,
            createdAt: item.createdAt,
            author: item.ownerName,
            note: item.Note_Content,
          });
        }
      });
    }
    const sortedNotes = parsedNotes.sort((a, b) => {
      if (moment(a.createdAt).isBefore(moment(b.createdAt))) {
        return notesOrder === "ASC" ? -1 : 1;
      }
      return notesOrder === "ASC" ? 1 : -1;
    });
    setNotes(sortedNotes);
  }, [notesQuery.data, notesOrder]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({
        behavior: state.hasScrolledAtStart ? "smooth" : "auto",
      });
      if (state.hasScrolledAtStart) {
        setState((prevState) => ({
          ...prevState,
          hasScrolledAtStart: true,
        }));
      }
    }
  }, [state.hasScrolledAtStart, notes]);

  const handleFormCancel = () => {
    setState((prevState) => ({
      ...prevState,
      open: false,
      mode: "add",
    }));
  };

  const handleFormSubmit = (note: string) => {
    if (!note) {
      return;
    }

    addNoteMutation.mutate({
      id: uuidv4(),
      Note_Content: note,
      parentId: parentId,
      zohoModule: moduleName,
    });

    setState((prevState) => ({
      ...prevState,
      open: false,
      mode: "add",
    }));
  };

  const handleDeleteConfirm = (noteId: string) => {
    if (noteId) {
      removeNoteMutation.mutate(noteId);
    }

    setDeleteId(null);
  };

  return (
    <Fragment>
      <div
        id="suggestionPortal"
        style={{
          height: "2px",
        }}
        ref={(el) => {
          container = el;
        }}
      ></div>
      <StyledWrapper>
        <StyledContentWrapper>
          {notesQuery.isLoading ? (
            <NotesLoading />
          ) : notes.length === 0 ? (
            <NotesEmpty />
          ) : (
            <Fragment>
              {notes.map((note, index) => {
                return (
                  <StyledNoteCard key={`notecard__${note.id}`}>
                    <NoteAuthor author={note.author ?? ""} seed={parentId} />
                    <Note
                      noteId={note.id}
                      value={note.note}
                      mentionsData={staffMentions}
                    />
                    <Typography className="CMuiNotesCardNoteDetails">
                      <FormattedMessage
                        id="Notes.NoteDetails"
                        defaultMessage="{date}"
                        values={{
                          date: note.createdAt
                            ? moment(note.createdAt).format(
                                "DD-MM-YYYY - HH:mm"
                              )
                            : "",
                        }}
                      />
                    </Typography>
                  </StyledNoteCard>
                );
              })}
              <div ref={bottomRef} />
            </Fragment>
          )}
        </StyledContentWrapper>
        <StyledFormWrapper>
          <NotesForm
            onFormSubmit={handleFormSubmit}
            onFormCancel={handleFormCancel}
            container={container}
            mentionsData={activeStaffMentions}
          />
        </StyledFormWrapper>
      </StyledWrapper>
      <ConfirmDialog
        open={deleteId !== null}
        title={
          <FormattedMessage
            id="NotesTable.DeleteTitle"
            defaultMessage="Eliminar nota"
          />
        }
        label={
          <FormattedMessage
            id="NotesTable.DeleteLabel"
            defaultMessage="¿Estás seguro que deseas eliminar la nota indicada?"
          />
        }
        onConfirm={() => {
          if (deleteId !== null) {
            handleDeleteConfirm(deleteId);
          }
        }}
        onCancel={() => {
          setDeleteId(null);
        }}
      />
    </Fragment>
  );
};

export default React.memo(
  (props: INotesProps) => {
    return <Notes {...props} />;
  },
  (prevProps, nextProps) => {
    if (
      prevProps.parentId !== nextProps.parentId ||
      prevProps.notesQuery !== nextProps.notesQuery
    ) {
      return false;
    }
    return true;
  }
);
